import { useCallback, useEffect, useState } from 'react';
import {
  connectInjectedWallet,
  getConnectedInjectedWallet,
  getInjectedWalletEnvironment,
  hasInjectedWalletProvider,
  switchInjectedWalletToBase,
} from '../../services/WalletService';

export function useInjectedTreasuryWallet({
  clearMessages,
  setError,
  setSuccess,
}) {
  const [connectedWallet, setConnectedWallet] = useState(null);
  const [walletAvailable, setWalletAvailable] = useState(false);
  const [walletEnvironment, setWalletEnvironment] = useState(() => getInjectedWalletEnvironment());

  useEffect(() => {
    setWalletAvailable(hasInjectedWalletProvider());
    let active = true;

    const hydrateWallet = async () => {
      try {
        const wallet = await getConnectedInjectedWallet();
        if (active) {
          setConnectedWallet(wallet);
        }
      } catch {
        if (active) {
          setConnectedWallet(null);
        }
      }
    };

    void hydrateWallet();

    return () => {
      active = false;
    };
  }, []);

  const connectWallet = useCallback(async () => {
    clearMessages();

    try {
      const wallet = await connectInjectedWallet();
      setConnectedWallet(wallet);
      setWalletAvailable(true);
      setWalletEnvironment(getInjectedWalletEnvironment());
      setSuccess(`${wallet?.walletLabel || 'Wallet'} connected successfully.`);
      return { success: true, data: wallet };
    } catch (err) {
      const message = err?.message || 'Failed to connect wallet.';
      if (/multiple wallet|detected providers/i.test(message)) {
        setError(`${message} Disable the wallets you are not using, or keep your preferred wallet as the active injected provider and try again.`);
      } else {
        setError(message);
      }
      return { success: false, message };
    }
  }, [clearMessages, setError, setSuccess]);

  const switchWalletToBase = useCallback(async () => {
    clearMessages();

    try {
      const wallet = await switchInjectedWalletToBase();
      setConnectedWallet(wallet);
      setWalletAvailable(true);
      setWalletEnvironment(getInjectedWalletEnvironment());
      setSuccess(`${wallet?.walletLabel || 'Wallet'} switched to Base.`);
      return { success: true, data: wallet };
    } catch (err) {
      const message = err?.message || 'Failed to switch wallet to Base.';
      if (/multiple wallet|detected providers/i.test(message)) {
        setError(`${message} Make sure the wallet you want to use is the active injected provider before switching networks.`);
      } else {
        setError(message);
      }
      return { success: false, message };
    }
  }, [clearMessages, setError, setSuccess]);

  return {
    connectedWallet,
    walletAvailable,
    walletEnvironment,
    connectWallet,
    switchWalletToBase,
  };
}
